import React from "react";
import { Link, useNavigate } from "react-router-dom";
import RegisterForm from "../components/RegisterForm";
import logo from "../assets/logo192.png";

const stats = [
  { value: "10k+", label: "Games indexed" },
  { value: "100%", label: "Free to use" },
  { value: "24/7", label: "Fresh recommendations" },
];

const perks = [
  "Save favourites across devices",
  "Build and share your own top lists",
  "Recommendations tuned to your taste",
];

export default function SignupPage() {
  const navigate = useNavigate();

  React.useEffect(() => {
    const poppinsId = "poppins-font";
    if (!document.getElementById(poppinsId)) {
      const link = document.createElement("link");
      link.id = poppinsId;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  const handleSuccess = () => {
    navigate("/auth/signin");
  };

  return (
    <div className="relative min-h-[calc(100vh-var(--header-height))] w-full flex items-stretch">
      {/* Left — brand showcase, hidden on small screens */}
      <div className="hidden lg:flex lg:w-[48%] relative overflow-hidden items-center px-12 xl:px-16 py-16">
        <img
          src="https://images.unsplash.com/photo-1511512578047-dfb367046420?auto=format&fit=crop&w=1600&q=80"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-brand-gradient-135 opacity-70 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-br from-surface/55 via-surface/35 to-surface/85" />
        <div className="absolute inset-0 gg-grid-backdrop opacity-60" />
        <div className="absolute -top-24 -right-16 w-80 h-80 rounded-full bg-brand-purple/30 blur-3xl animate-pulseGlow" />
        <div className="absolute -bottom-24 -left-10 w-72 h-72 rounded-full bg-brand-gold/20 blur-3xl animate-floaty" />

        {/* brand lockup */}
        <Link to="/" className="group absolute top-8 left-12 xl:left-16 z-10 flex items-center gap-2.5">
          <img
            src={logo}
            alt="Game Goblin"
            className="h-10 w-10 object-contain drop-shadow-[0_2px_10px_rgba(151,1,251,0.5)] transition-transform duration-300 group-hover:scale-105"
          />
          <span className="font-heading font-extrabold text-lg text-white">
            Game<span className="gg-gradient-text">Goblin</span>
          </span>
        </Link>

        <div className="relative z-10 max-w-md animate-fadeUp">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold text-brand-gold-soft backdrop-blur-sm mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-gold" />
            Free forever · no card required
          </span>
          <h1 className="font-heading font-extrabold text-white text-4xl xl:text-5xl leading-[1.1] mb-4">
            Join the hunt for
            <br />
            <span className="gg-gradient-text">great games.</span>
          </h1>
          <p className="text-white/75 text-base xl:text-lg mb-8 leading-relaxed">
            Create a free account to save favourites, build top lists, and get
            recommendations tuned to your taste.
          </p>

          <div className="space-y-2.5 mb-8">
            {perks.map((p) => (
              <div key={p} className="flex items-center gap-3 text-white/85 text-sm">
                <span className="flex items-center justify-center h-5 w-5 rounded-full bg-neon-lime/15 text-neon-lime text-xs">✓</span>
                {p}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3">
            {stats.map((s) => (
              <div
                key={s.label}
                className="rounded-2xl bg-white/[0.07] border border-white/15 shadow-inset-line px-3 py-4 text-center"
              >
                <p className="font-heading font-extrabold text-xl sm:text-2xl gg-gradient-text">{s.value}</p>
                <p className="text-white/60 text-xs mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right — form */}
      <div className="relative flex-1 flex items-center justify-center px-4 sm:px-8 py-12">
        <div className="absolute inset-0 gg-grid-backdrop opacity-[0.15] pointer-events-none" />
        <div className="absolute top-1/3 left-1/4 h-64 w-64 rounded-full bg-brand-purple/15 blur-3xl pointer-events-none" />

        <div className="relative w-full max-w-[420px] animate-fadeUp">
          {/* mobile brand */}
          <Link to="/" className="lg:hidden flex items-center justify-center gap-2.5 mb-8">
            <img src={logo} alt="Game Goblin" className="h-10 w-10 object-contain drop-shadow-[0_2px_10px_rgba(151,1,251,0.5)]" />
            <span className="font-heading font-extrabold text-lg text-white">
              Game<span className="gg-gradient-text">Goblin</span>
            </span>
          </Link>

          <div className="flex flex-col items-center lg:items-start mb-7">
            <div className="h-14 w-14 rounded-2xl bg-brand-gradient flex items-center justify-center text-2xl shadow-glow-purple mb-4">
              🎮
            </div>
            <h2 className="font-heading font-extrabold text-white text-2xl sm:text-3xl text-center lg:text-left">
              Create your account
            </h2>
            <p className="text-white/55 text-sm mt-1.5 text-center lg:text-left">
              It only takes a few seconds, no credit card, ever.
            </p>
          </div>

          <div className="gg-neon-ring rounded-3xl border border-white/10 bg-surface-card/55 backdrop-blur-xl shadow-card shadow-inset-line p-5 sm:p-7">
            <RegisterForm onSuccess={handleSuccess} />
          </div>

          <p className="text-center text-white/55 text-sm mt-6">
            Already have an account?{" "}
            <Link to="/auth/signin" className="text-brand-gold font-semibold hover:underline">
              Sign in
            </Link>
          </p>
          <p className="text-center mt-3">
            <Link to="/" className="text-white/40 text-xs hover:text-white/70 transition-colors">
              ← Back to home
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
